import { useApolloClient } from '@apollo/client';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  styled,
  TextField,
} from '@mui/material';
import { Close } from '@styled-icons/evaicons-solid/Close';
import { PartnerPlaceholderImage } from 'assets/img';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import useComponentItems from 'FeatureModules/ComponentChain/hooks/useComponentItems';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { BASE_PARTNER_COMPANY_FRAGMENT } from 'graphql/secure/fragments';
import useIsOverflow from 'hooks/useIsOverflow';
import debounce from 'lodash/debounce';
import React, { ChangeEvent, FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { stopPropagation } from 'utils/event.utils';
import AddEditComponentDialog from '../Dialogs/AddEditComponentDialog';
import PaperWithAddItem from 'DesignSystem/Inputs/Paper/Paper';

type IBaseSiteSelectionProps = {
  placeholder?: string;
  label?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fontSize?: number;
  hasError?: boolean;
  errorMessage?: string;
  addNewItem?: boolean;

  /**
   * Filters the component selection by owner
   */
  filterByOwnedById?: string;
  /**
   * Filters the component selection by raw materials
   */
  filterByRawMaterialIds?: string[];

  fullWidth?: boolean;
  'data-testid'?: string;
} & (
  | {
      multiple?: false;
      defaultValue?: IComponentItem;
      onSelectionChange: (site: IComponentItem | undefined) => void;
    }
  | {
      multiple: true;
      defaultValue?: IComponentItem[];
      onSelectionChange: (sites: IComponentItem[] | undefined) => void;
    }
);

const SIZE_RECORD: Record<'small' | 'medium', number> = {
  small: 30,
  medium: 40,
};

const StyledSelect = styled(Select<string[]>)<{ size: 'small' | 'medium' }>(({ size }) => ({
  '&.MuiInputBase-root': {
    height: SIZE_RECORD[size],
    '& .select-clear-icon-btn': {
      visibility: 'hidden',
    },
    '&:hover .select-clear-icon-btn': {
      visibility: 'visible',
    },
  },
  '& .MuiSelect-select': {
    backgroundColor: 'transparent',
    height: SIZE_RECORD[size],
  },
  '& .MuiChip-sizeSmall': {
    margin: 4,
  },
}));

const StyledSelectInputContainer = styled(Box)(() => ({
  // Width of the icon button need to be excluded
  maxWidth: 'calc(100% - 60px)',
  overflow: 'hidden',
  minHeight: '1.1876em',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const Searchbar = styled(TextField)(({ theme }) => ({
  width: '100%',
  '&.MuiTextField-root': {
    padding: '0 8px 8px 8px',
    background: theme.custom.themeColors.white,
    '& .MuiInputBase-formControl': {
      paddingRight: 0,
    },
    '& .MuiInputBase-input': {
      padding: '10px 16px 10px',
    },
    '& .MuiInput-underline:before, & .MuiInput-underline:hover:before': {
      borderBottom: `1px solid ${theme.custom.themeColors.grayScale[40]}`,
    },
    '& .MuiInput-underline:after': {
      borderBottom: 0,
    },
  },
}));

const Placeholder = styled(InputLabel)<{ styledSize: 'small' | 'medium' }>(
  ({ theme, styledSize }) => ({
    zIndex: 1,
    fontSize: 12,
    fontWeight: 500,
    color: `${theme.palette.grey[500]} !important`,
    '&.MuiInputLabel-outlined.MuiInputLabel-root': {
      transform: `translate(14px, ${styledSize === 'small' ? 9 : 12}px) scale(1)`,
    },
  })
);

const StyledLabel = styled(InputLabel)<{ styledSize: 'small' | 'medium' }>(
  ({ theme, styledSize }) => ({
    zIndex: 9,
    top: 3,
    fontSize: styledSize === 'small' ? 12 : 14,
    lineHeight: '1.2em !important',
  })
);

const StyledChip = styled(Box)<{ size: 'small' | 'medium' }>(({ size }) => ({
  display: 'flex',
  alignItems: 'center',
  height: size === 'small' ? 12 : 24,
  padding: '0 8px',
  borderRadius: 4,
  backgroundColor: '#F0F0F0',
  fontSize: size === 'small' ? 12 : 14,
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  '&.MuiFormHelperText-root': {
    marginTop: -2,
  },
}));

const StyledMenuItem = styled(MenuItem)<{ borderBottom?: Tbooleanish }>(
  ({ theme, borderBottom }) => ({
    borderBottom:
      borderBottom === 'true' ? `1px solid ${theme.custom.themeColors.grayScale[40]}` : 'none',
  })
);

const LoadingContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: 42,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

interface IBaseSiteLitItemContent {
  component: IComponentItem;
}

const ComponentLitItemContent: FC<IBaseSiteLitItemContent> = ({ component }) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasOverflow = useIsOverflow(ref);

  return (
    <FlexBox height={30} gap={1} display="flex" alignItems="center" maxWidth="calc(100% - 28px)">
      <FlexBox ref={ref} gap={0.5} maxWidth={hasOverflow ? '40%' : undefined}>
        {component.image ? (
          <Image width={20} height={20} image={component.image} />
        ) : (
          <Icon name="component" size="medium" color="gray-80" />
        )}
        <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
          {component.title}
        </ThemeTypography>
      </FlexBox>

      <FlexBox gap={0.5} maxWidth={hasOverflow ? '30%' : undefined}>
        <Icon name="raw-material" />
        {component.rawMaterials.reduce(
          (prev, { title }) => (prev.length ? `${prev}, ${title}` : title),
          ''
        )}
      </FlexBox>

      {component.ownedBy && (
        <FlexBox gap={1} maxWidth={hasOverflow ? '30%' : undefined}>
          {component.ownedBy.logo ? (
            <Image
              width={20}
              height={20}
              image={component.ownedBy.logo}
              backupImageUrl={PartnerPlaceholderImage}
            />
          ) : (
            <Icon name="partner" size="large" color="blue-ice" />
          )}
          <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" autoOverflow>
            {component.ownedBy?.name}
          </ThemeTypography>
        </FlexBox>
      )}
    </FlexBox>
  );
};

const ComponentSelection: FC<IBaseSiteSelectionProps> = ({
  defaultValue,
  size = 'medium',
  multiple,
  hasError,
  label,
  fullWidth,
  errorMessage,
  filterByOwnedById,
  filterByRawMaterialIds,
  addNewItem = true,
  placeholder = 'Select a site',
  disabled,
  'data-testid': dataTestId,
  onSelectionChange,
}) => {
  const selectionRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useIsOverflow(selectionRef);
  const client = useApolloClient();
  const [createComponentDialogOpen, setCreateComponentDialogOpen] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selection, setSelection] = useState<IComponentItem[]>(
    Array.isArray(defaultValue) ? defaultValue : defaultValue ? [defaultValue] : []
  );

  const {
    components,
    loading,
    refetch,
    hasNextPage,
    setQuery,
    handleSearchTermChange,
    handlePageEndReached,
  } = useComponentItems({
    defaultOwnedById: filterByOwnedById,
    defaultRawMaterialIds: filterByRawMaterialIds,
    skipUseQueryParams: true,
  });

  const [internalSearchTerm, setInternalSearchTerm] = useState<string>('');

  useEffect(
    () => debounce(() => handleSearchTermChange(internalSearchTerm), 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalSearchTerm]
  );

  /**
   * When the filters change, the selection needs to be cleared since the selected component might not be in the new filter results. The user will need to reselect the component if they want to keep it after the filter change.
   */
  useEffect(() => {
    setQuery({
      partnerId: filterByOwnedById,
      rawMaterialIds: filterByRawMaterialIds,
    });
  }, [filterByOwnedById, filterByRawMaterialIds, setQuery]);

  const defaultOwnedBy = filterByOwnedById
    ? client.readFragment({
        fragment: BASE_PARTNER_COMPANY_FRAGMENT,
        id: `Company:${filterByOwnedById}`,
        fragmentName: 'basePartnerCompanyValues',
      })
    : null;

  const componentsWithoutSelection = useMemo(() => {
    return components.filter(
      component => !selection.some(selected => selected.id === component.id)
    );
  }, [components, selection]);

  const handleSelectionChange = async (event: SelectChangeEvent<string[]>) => {
    const newSelection = event.target.value?.length
      ? components.filter(({ id }) => event.target.value.includes(id))
      : [];
    setSelection(newSelection);
    if (multiple) {
      onSelectionChange(newSelection.length ? newSelection : undefined);
    } else {
      onSelectionChange(newSelection.length ? newSelection[0] : undefined);
    }
  };

  const handleNewComponentCreated = async (component: IComponentItem) => {
    setSelection(prev => {
      if (multiple) {
        onSelectionChange(prev.length ? [...prev, component] : [component]);
        return [...prev, component];
      }
      onSelectionChange(component);
      return [component];
    });
    setCreateComponentDialogOpen(false);
    await refetch();
  };

  const getSelectedItem = (id: string) =>
    selection?.find(item => item.id === id) || components.find(item => item.id === id);

  const renderSelectedItems = (selectedItems: string[]) => (
    <FlexBox gap={2} height={SIZE_RECORD[size]}>
      <StyledSelectInputContainer ref={selectionRef}>
        {selectedItems.map(id => {
          const item = getSelectedItem(id);
          return item ? <ComponentLitItemContent key={id} component={item} /> : null;
        })}
      </StyledSelectInputContainer>
      {/* Not perfect since it doesn't account for all selected items and count the displayed ones */}
      {selectedItems.length > 1 && hasOverflow && (
        <StyledChip size={size}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
            +{selectedItems.length - 1} more
          </ThemeTypography>
        </StyledChip>
      )}
    </FlexBox>
  );

  const clearSearchValue = () => {
    setInternalSearchTerm('');
    handleSearchTermChange('');
  };

  const clearSelection = () => {
    setSelection([]);
    onSelectionChange(undefined);
  };

  const handleSearchValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setInternalSearchTerm(event.target.value);
  };

  const handleMenuClose = () => {
    setIsOpen(false);
    clearSearchValue();
  };

  const handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  const handleEndReached = (inView: boolean) => {
    if (inView && hasNextPage) {
      handlePageEndReached?.();
    }
  };

  return (
    <>
      {!components && loading ? (
        <Box position="relative">
          <Skeleton width="100%" height={size === 'small' ? 40 : 56} />
        </Box>
      ) : (
        <FormControl fullWidth size={size} error={hasError}>
          {label && (
            <StyledLabel shrink variant="outlined" styledSize={size}>
              {label}
            </StyledLabel>
          )}

          {!selection?.length && (
            <Placeholder variant="outlined" styledSize={size} shrink={!label}>
              {placeholder}
            </Placeholder>
          )}

          <StyledSelect
            label={label}
            variant="outlined"
            value={selection.map(site => site.id)}
            multiple={multiple}
            size={size}
            endAdornment={
              !!selection.length ? (
                <InputAdornment
                  position="end"
                  component={Box}
                  mr={2.5}
                  className="select-clear-icon-btn"
                >
                  <IconButton onClick={clearSelection}>
                    <Icon name="cross" size="small" color="gray-80" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
            MenuProps={{
              autoFocus: false,
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: {
                component: PaperWithAddItem,
                maxHeight: 300,
                SearchComponent: (
                  <Searchbar
                    autoFocus
                    fullWidth
                    value={internalSearchTerm}
                    placeholder="Type to search sites & clusters"
                    onChange={handleSearchValueChange}
                    onKeyDown={handleSearchKeyDown}
                    // Prevent searchbar from being selected
                    InputProps={{
                      endAdornment: loading ? (
                        <InputAdornment position="end" data-testid={`${dataTestId}-loading`}>
                          <Box position="relative" width={42}>
                            <Loader size={16} />
                          </Box>
                        </InputAdornment>
                      ) : (
                        <InputAdornment position="start" data-testid={`${dataTestId}-clear-button`}>
                          <IconButton onClick={clearSearchValue} disabled={!internalSearchTerm}>
                            {!!internalSearchTerm ? (
                              <Close size={16} color="gray-80" />
                            ) : (
                              <Icon name="search" color="gray-80" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      onClick: stopPropagation,
                    }}
                  />
                ),
                infoMessage:
                  'The components are filtered by the selected owner on this activity, or have no owner assigned and which share the raw material of the selected site focus or additional material.',
                ActionComponent: addNewItem ? (
                  <>
                    <MenuItem
                      onMouseDown={() => setCreateComponentDialogOpen(true)}
                      sx={{ boxShadow: '0 -1px 3px rgba(0,0,0,0.12)' }}
                    >
                      <Icon name="plus" mr={1} />
                      Add new component
                    </MenuItem>
                  </>
                ) : undefined,
              },
            }}
            open={isOpen}
            fullWidth={fullWidth}
            onChange={handleSelectionChange}
            renderValue={renderSelectedItems}
            onOpen={() => setIsOpen(true)}
            onClose={() => handleMenuClose()}
            disabled={disabled}
            data-testid={dataTestId}
          >
            {selection.length > 0 &&
              selection.map((site, index) => (
                <StyledMenuItem
                  key={site.id}
                  value={site.id}
                  borderBottom={Booleanish(index === selection.length - 1)}
                >
                  <ComponentLitItemContent component={site} />
                </StyledMenuItem>
              ))}

            {!componentsWithoutSelection.length && loading && (
              <LoadingContainer>
                <Loader size={20} />
              </LoadingContainer>
            )}

            {componentsWithoutSelection.length > 0 &&
              componentsWithoutSelection.map(component => (
                <StyledMenuItem key={component.id} value={component.id}>
                  <ComponentLitItemContent component={component} />
                </StyledMenuItem>
              ))}

            {hasNextPage && (
              <InView
                trackVisibility
                as="div"
                delay={200}
                threshold={0.8}
                initialInView
                onChange={handleEndReached}
              >
                <LoadingContainer>
                  <Loader size={20} />
                </LoadingContainer>
              </InView>
            )}

            {!loading && !components?.length && (
              <Box height={30} mt={2} mb={1} ml={2}>
                <ThemeTypography variant="BODY_LARGE" color="GRAY_40">
                  No results
                </ThemeTypography>
              </Box>
            )}
          </StyledSelect>
          {hasError && <StyledFormHelperText>{errorMessage}</StyledFormHelperText>}
        </FormControl>
      )}

      {/* Workaround: This dialog needs to live here and NOT in the context since the callback is changing the state */}
      <AddEditComponentDialog
        open={createComponentDialogOpen}
        partner={defaultOwnedBy}
        selectableRawMaterialIds={filterByRawMaterialIds}
        title="Create component"
        onComponentCreated={handleNewComponentCreated}
        onClose={() => setCreateComponentDialogOpen(false)}
      />
    </>
  );
};

export default ComponentSelection;
